import { Link } from "react-router-dom";
import { Phone, PhoneCall, ArrowRight, Check, X, Bug, Droplets, Moon, Search, SearchCheck, BedDouble, Sparkles, ClipboardList, BadgeEuro, ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import Seo from "@/components/Seo";
import CtaSection from "@/components/CtaSection";

const BED_BUG_IMG = "/assets/punaise.jpg";

const SIGNS = [
    { icon: Bug, title: "4 à 7 mm, aplatie", desc: "La Cimex lectularius fuit la lumière et se glisse dans les moindres interstices. Active la nuit, elle se nourrit de sang humain." },
    { icon: Droplets, title: "Taches de sang", desc: "De petites taches de sang sur vos draps et votre literie sont un signe typique de leur présence." },
    { icon: Search, title: "Miettes noires", desc: "Leurs déjections ressemblent à de petites miettes noires déposées sur le matelas et le sommier." },
    { icon: Moon, title: "Morsures nocturnes", desc: "Éruptions cutanées et démangeaisons au réveil, souvent alignées sur les zones découvertes du corps." },
];

const TREATMENTS = [
    {
        name: "Vapeur sèche 180°C",
        tag: "La plus efficace",
        passages: "1 seul passage",
        price: "dès 350 €",
        desc: "Traitement de l'ensemble du logement à la vapeur sèche : punaises, larves et œufs éliminés instantanément. Canapé 90 €, lit 100 €.",
        pros: ["Solution 100% naturelle", "Vous pouvez rester dans le logement", "Sans danger pour enfants, animaux et personnes âgées"],
        cons: [],
        highlight: true,
        testid: "punaises-treatment-vapeur",
    },
    {
        name: "Insecticides professionnels",
        tag: "L'économique",
        passages: "2 passages requis",
        price: "dès 180 €",
        desc: "Pulvérisation d'insecticides professionnels rémanents dans le respect des normes écologiques. Tarif selon la superficie.",
        pros: ["Solution économique"],
        cons: ["Déconseillée aux personnes fragiles", "Vêtements à traiter séparément"],
        highlight: false,
        testid: "punaises-treatment-chimique",
    },
    {
        name: "Fumigène effet choc",
        tag: "Longue rémanence",
        passages: "1 seul passage",
        price: "160 €",
        desc: "Combustible à effet prolongé et résiduel qui neutralise même les punaises résistantes aux insecticides traditionnels.",
        pros: ["Efficace sur les punaises résistantes", "À partir de 10 m²"],
        cons: ["Aération du logement nécessaire après traitement"],
        highlight: false,
        testid: "punaises-treatment-fumigene",
    },
];

const FAQ = [
    {
        q: "Comment savoir si j'ai des punaises de lit ?",
        a: "Éruptions cutanées et démangeaisons dans la journée, petites taches de sang sur la literie, traces de miettes noires dans le lit : vous êtes très probablement victime de punaises de lit. Un diagnostic professionnel confirme leur présence.",
    },
    {
        q: "Comment les punaises de lit arrivent-elles chez moi ?",
        a: "Elles voyagent cachées dans les valises, les vêtements, les meubles neufs ou d'occasion. Un séjour à l'hôtel ou en gîte, un déménagement ou un simple transport peuvent suffire à les introduire chez vous.",
    },
    {
        q: "Qui doit payer le traitement, locataire ou propriétaire ?",
        a: "Selon la loi Elan de 2018, si l'infestation apparaît entre 6 et 12 mois après l'emménagement, elle est en principe à la charge du bailleur. Au-delà d'un an d'occupation et si seul votre logement est touché, les frais incombent généralement au locataire. En situation précaire, rapprochez-vous de votre bailleur HLM ou de votre mairie.",
    },
    {
        q: "Que faire après une désinsectisation ?",
        a: "Aspirez fréquemment pendant plusieurs semaines et jetez immédiatement le sac à la poubelle. Continuez les vérifications régulières même en l'absence de signes visibles. Nous recommandons une housse de matelas anti-punaises pendant au moins 2 ans.",
    },
    {
        q: "Combien de temps vit une punaise de lit ?",
        a: "Son espérance de vie moyenne est de 6 mois, mais elle peut survivre jusqu'à 18 mois sans repas en diapause. C'est pourquoi un traitement professionnel rémanent est indispensable.",
    },
    {
        q: "Êtes-vous discrets lors de vos interventions ?",
        a: "Absolument. Votre vie privée est précieuse : nos interventions à domicile se font en toute discrétion, sans mention visible de la nature de notre visite.",
    },
];

const WHY_US = [
    { icon: SearchCheck, title: "Détection fiable et traitement efficace", desc: "Nous sommes formés conformément aux recommandations du gouvernement et utilisons des produits professionnels." },
    { icon: BadgeEuro, title: "Prix justes et transparents", desc: "Une grille tarifaire que nous respectons, appliquée uniformément tous les jours de la semaine, sans exception. Qualité de service et transparence tarifaire sont nos engagements." },
    { icon: ShieldCheck, title: "Protection du client et garanties", desc: "Nous vous assurons un service professionnel de qualité. En cas de non-fonctionnement, nous offrons une possibilité de garantie de repassage." },
    { icon: PhoneCall, title: "Suivi avant et après l'intervention", desc: "Vous pouvez nous contacter pour obtenir des renseignements ou des conseils avant ou après l'intervention." },
];

const Punaises = () => {
    return (
        <div data-testid="punaises-page">
            <Seo
                title="Traitement punaises de lit en Bretagne | Vapeur 180°C dès 180 € | Eradikbreizh"
                description="Élimination des punaises de lit : vapeur sèche 180°C 100% naturelle, insecticides professionnels, fumigène. Intervention sous 2h, 7j/7 en Bretagne. Devis gratuit."
            />
            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-24 pt-40 md:pb-32 md:pt-48">
                <div className="absolute -right-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
                <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
                    <div>
                        <MaskedLine delay={0.1}>
                            <span className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 shadow-sm">
                                <BedDouble className="h-4 w-4 text-brand" />
                                Spécialiste punaises de lit
                            </span>
                        </MaskedLine>
                        <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl">
                            <MaskedLine delay={0.25}>Punaises</MaskedLine>
                            <MaskedLine delay={0.4}>
                                <span className="text-brand">de lit</span>
                            </MaskedLine>
                        </h1>
                        <MaskedLine delay={0.55}>
                            <p className="mt-8 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
                                La punaise de lit ne doit pas vous gâcher la vie. Traitement vapeur 180°C,
                                insecticides rémanents et suivi garanti, partout en Bretagne.
                            </p>
                        </MaskedLine>
                        <MaskedLine delay={0.7}>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link
                                    data-testid="punaises-hero-devis-button"
                                    to="/tarifs"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                                >
                                    Voir les tarifs
                                    <ArrowRight className="h-5 w-5" />
                                </Link>
                                <a
                                    data-testid="punaises-hero-phone-button"
                                    href="tel:+33255992243"
                                    className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                                >
                                    <Phone className="h-5 w-5 text-brand" />
                                    02 55 99 22 43
                                </a>
                            </div>
                        </MaskedLine>
                    </div>
                    <Reveal delay={0.3} className="relative">
                        <div className="float-slow relative overflow-hidden rounded-3xl border border-black/10 shadow-[0_30px_80px_rgba(10,15,36,0.15)]">
                            <img src={BED_BUG_IMG} alt="Punaise de lit en gros plan" className="h-[26rem] w-full object-cover sm:h-[30rem]" />
                            <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 rounded-2xl border border-black/10 bg-white/85 px-5 py-4 backdrop-blur-xl">
                                <p className="font-display text-2xl font-black text-brand">180°C</p>
                                <p className="text-xs uppercase tracking-widest text-slate-600">Vapeur sèche</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* POURQUOI NOUS */}
            <section className="bg-white py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Pourquoi Eradikbreizh</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Les meilleurs traitements pour <span className="text-brand">en finir avec les punaises de lit</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                            Les punaises de lit peuvent causer de nombreux désagréments dans nos foyers. Ces petits insectes nocturnes se nourrissent de notre sang et peuvent provoquer démangeaisons et irritations cutanées. Si vous êtes confronté à une infestation, ne désespérez pas : nous vous proposons des traitements efficaces pour éliminer ces nuisibles de votre logement.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {WHY_US.map((w, i) => (
                            <Reveal key={w.title} delay={0.1 * i} className="h-full">
                                <div className="h-full rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/40">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                        <w.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-6 font-display text-xl font-bold">{w.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{w.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SIGNS */}
            <section className="bg-mist py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Reconnaître l'ennemi</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Les signes qui <span className="text-brand">ne trompent pas</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {SIGNS.map((s, i) => (
                            <Reveal key={s.title} delay={0.1 * i} className="h-full">
                                <div className="h-full rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/40">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                        <s.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-6 font-display text-xl font-bold">{s.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TREATMENTS */}
            <section className="py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Nos traitements</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            3 protocoles, <span className="text-brand">un objectif : zéro punaise</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid gap-8 lg:grid-cols-3">
                        {TREATMENTS.map((t, i) => (
                            <Reveal key={t.name} delay={0.12 * i} className="h-full">
                                <div
                                    data-testid={t.testid}
                                    className={`relative flex h-full flex-col rounded-3xl border p-8 transition-[border-color,transform] duration-500 hover:-translate-y-2 ${
                                        t.highlight
                                            ? "border-brand bg-brand text-white shadow-[0_20px_60px_rgba(27,67,189,0.35)]"
                                            : "border-black/10 bg-white shadow-sm hover:border-brand/40"
                                    }`}
                                >
                                    <span className={`inline-flex w-fit rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest ${t.highlight ? "bg-white text-brand" : "bg-brand/10 text-brand"}`}>
                                        {t.tag}
                                    </span>
                                    <h3 className={`mt-5 font-display text-2xl font-black uppercase tracking-tight ${t.highlight ? "text-white" : "text-night"}`}>{t.name}</h3>
                                    <p className={`mt-2 text-xs font-bold uppercase tracking-[0.25em] ${t.highlight ? "text-white/70" : "text-brand"}`}>{t.passages}</p>
                                    <p className={`mt-3 font-display text-3xl font-black ${t.highlight ? "text-white" : "text-brand"}`}>{t.price}</p>
                                    <p className={`mt-4 text-sm leading-relaxed ${t.highlight ? "text-white/85" : "text-slate-600"}`}>{t.desc}</p>
                                    <ul className="mt-6 flex-1 space-y-3">
                                        {t.pros.map((p) => (
                                            <li key={p} className={`flex items-start gap-3 text-sm ${t.highlight ? "text-white/90" : "text-slate-700"}`}>
                                                <Check className={`mt-0.5 h-4 w-4 shrink-0 ${t.highlight ? "text-white" : "text-brand"}`} />
                                                {p}
                                            </li>
                                        ))}
                                        {t.cons.map((c) => (
                                            <li key={c} className={`flex items-start gap-3 text-sm ${t.highlight ? "text-white/60" : "text-slate-400"}`}>
                                                <X className={`mt-0.5 h-4 w-4 shrink-0 ${t.highlight ? "text-white/60" : "text-red-400"}`} />
                                                {c}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <div className="mt-12 flex items-start gap-4 rounded-3xl border border-brand/30 bg-brand/5 p-8">
                            <Sparkles className="mt-1 h-6 w-6 shrink-0 text-brand" />
                            <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
                                <strong className="font-display">Notre protocole :</strong> après avoir vidé la chambre, nous pulvérisons
                                l'ensemble des zones (le matelas est traité à la vapeur). Une seconde intervention est prévue 15 jours
                                plus tard si nécessaire, et nos experts restent disponibles par téléphone pour vous accompagner.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <Link
                            to="/protocole-punaises"
                            data-testid="punaises-protocole-banner"
                            className="mt-6 flex items-center justify-between gap-6 rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-[border-color,transform] duration-300 hover:border-brand/50 hover:scale-[1.01]"
                        >
                            <div className="flex items-center gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                    <ClipboardList className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="font-display text-xl font-black uppercase tracking-tight">Protocole avant & après traitement</p>
                                    <p className="mt-1 text-sm text-slate-600">Préparation obligatoire du logement, à lire avant notre intervention.</p>
                                </div>
                            </div>
                            <ArrowRight className="h-6 w-6 shrink-0 text-brand" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-mist py-24 md:py-32">
                <div className="mx-auto max-w-4xl px-5 sm:px-8">
                    <Reveal><SectionTag>Questions fréquentes</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Tout savoir sur les <span className="text-brand">punaises de lit</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2} className="mt-12">
                        <Accordion type="single" collapsible className="space-y-4" data-testid="punaises-faq">
                            {FAQ.map((item, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`item-${i}`}
                                    data-testid={`punaises-faq-item-${i + 1}`}
                                    className="rounded-2xl border border-black/10 bg-white px-6 shadow-sm transition-colors data-[state=open]:border-brand/40"
                                >
                                    <AccordionTrigger className="py-6 text-left font-display text-lg font-bold hover:no-underline">
                                        {item.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-6 text-sm leading-relaxed text-slate-600 sm:text-base">
                                        {item.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            <CtaSection
                title="Des punaises dans votre lit ?"
                subtitle="Chaque nuit compte. Contactez-nous pour un diagnostic et une intervention sous 2h, 7j/7 partout en Bretagne."
            />
        </div>
    );
};

export default Punaises;
